import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { api, setCsrf, type Me } from "./api.js";
import { LoginGate } from "./components/LoginGate.js";
import { KeelsonMark } from "./components/Logo.js";
import { RegisterGateway } from "./components/RegisterGateway.js";
import { RegisterWorkspace } from "./components/RegisterWorkspace.js";
import type { CSSProperties, JSX } from "react";

function Banner(): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const toggleStyle: CSSProperties = {
    cursor: "pointer",
    color: "inherit",
    fontSize: "smaller",
    marginLeft: "8px",
    background: "none",
    border: "none",
    textDecoration: "underline",
  };

  if (!expanded) {
    return (
      <div
        data-testid="sample-banner"
        role="alert"
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "2px 10px",
          borderRadius: "9999px",
          background: "#4a2c05",
          border: "1px solid #d97706",
          color: "#fde68a",
          fontSize: "0.85rem",
          margin: "8px 0",
        }}
      >
        ⚠ SAMPLE — not for submission
        <button style={toggleStyle} onClick={() => setExpanded(true)}>
          [expand]
        </button>
      </div>
    );
  }

  return (
    <div className="banner" data-testid="sample-banner" role="alert">
      <strong>SAMPLE / UNVERIFIED ruleset.</strong> Not for regulatory submission. A green result
      means &ldquo;passes our sample rules&rdquo;, <em>never</em> &ldquo;your NCA accepts&rdquo;.
      Field codes and checks are directional and must be verified against primary sources (ITS (EU)
      2024/2956 + EBA DPM).
      <button style={toggleStyle} onClick={() => setExpanded(false)}>
        [collapse]
      </button>
    </div>
  );
}

function AuthedShell({ me }: { me: Me }): JSX.Element {
  // KEE-112: logout is now a POST (CSRF-protected). Fetch the Auth0 logout URL, clear local
  // CSRF state, then redirect. Falls back to home if the session is already expired.
  async function logout(): Promise<void> {
    try {
      const data = await api.logout();
      setCsrf(null);
      window.location.href = data.redirectUrl;
    } catch {
      setCsrf(null);
      window.location.href = "/";
    }
  }

  return (
    <div className="app">
      <Banner />
      <header>
        <div className="logo-lockup">
          <KeelsonMark size={28} />
          <div>
            <div className="logo-wordmark">Keelson</div>
            <div className="logo-sub">DORA RoI Linter</div>
          </div>
        </div>
        <div className="account" data-testid="account">
          <span data-testid="user-email">{me.user.email}</span>
          <button
            className="ghost"
            data-testid="logout-btn"
            onClick={() => {
              void logout();
            }}
          >
            Log out
          </button>
        </div>
      </header>

      <Routes>
        <Route path="/" element={<RegisterGateway />} />
        <Route path="/r/:registerId" element={<RegisterWorkspace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export function App(): JSX.Element {
  const [me, setMe] = useState<Me | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    // Probe the session on load. 401 -> show the login gate.
    api
      .me()
      .then((m) => {
        setCsrf(m.csrfToken);
        setMe(m);
      })
      .catch(() => setMe(null))
      .finally(() => setAuthReady(true));
  }, []);

  if (!authReady) {
    return (
      <div className="login-screen">
        <p className="muted" data-testid="auth-loading">
          Loading…
        </p>
      </div>
    );
  }

  if (me === null) return <LoginGate />;
  return <AuthedShell me={me} />;
}
